<template>
  <div class="admin-list-page employee-list">
    <div class="admin-page-heading mb-5">
      <div>
        <div class="text-overline primary--text font-weight-bold">Team</div>
        <h1 class="text-h4 font-weight-bold mb-1">Employees</h1>
        <p class="admin-muted mb-0">{{ count }} employee{{ count === 1 ? '' : 's' }} across your business</p>
      </div>
      <div class="employee-heading-actions">
        <v-btn outlined color="primary" rounded min-height="44" :to="{ name: 'employee-role-list' }">
          <v-icon left>mdi-account-key-outline</v-icon>Manage roles
        </v-btn>
        <v-btn v-if="!hideCreate" color="primary" depressed rounded min-height="44" @click="$router.push({ name: 'employee-create' })">
          <v-icon left>mdi-account-plus-outline</v-icon>Add employee
        </v-btn>
      </div>
    </div>

    <v-card outlined class="admin-panel rounded-xl">
      <v-card-text class="pa-4 pa-sm-6">
        <div class="employee-search mb-5">
          <v-text-field
            v-model="searchInput"
            aria-label="Search employees"
            clearable dense outlined rounded hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search by name, email, or phone"
            class="admin-list-search"
            @keydown.enter.prevent="applySearch"
            @click:clear="clearSearch"
          />
          <v-btn color="primary" depressed rounded min-height="44" :loading="loading" @click="applySearch">Search</v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" text class="rounded-lg">{{ errorMessage }}</v-alert>

        <div v-if="$vuetify.breakpoint.smAndDown" class="employee-card-list">
          <v-skeleton-loader v-if="loading" type="list-item-avatar-three-line@4" />
          <v-card v-for="employee in employees" v-else :key="employee.id" outlined class="employee-card rounded-xl mb-3 pa-4">
            <div class="d-flex align-start">
              <v-avatar color="primary" size="44" class="employee-card__avatar mr-3">
                <span class="white--text font-weight-bold">{{ initials(employee) }}</span>
              </v-avatar>
              <div class="min-width-0 flex-grow-1">
                <div class="font-weight-bold text-subtitle-1" v-html="$options.filters.highlight(employeeName(employee), search)" />
                <div v-if="employee.phoneNumber" class="text-body-2 admin-muted text-truncate" v-html="$options.filters.highlight(employee.phoneNumber, search)" />
                <div v-if="employee.email" class="text-body-2 admin-muted text-truncate" v-html="$options.filters.highlight(employee.email, search)" />
                <div v-if="!employee.phoneNumber && !employee.email" class="text-body-2 admin-muted">No contact information</div>
              </div>
              <v-btn icon :aria-label="`Edit ${employeeName(employee)}`" @click="editEmployee(employee)"><v-icon>mdi-pencil-outline</v-icon></v-btn>
            </div>
            <div class="text-caption font-weight-bold admin-muted mt-4 mb-2">Locations &amp; roles ({{ (employee.ActiveLocations || []).length }})</div>
            <div class="employee-assignments" role="list" :aria-label="`Location assignments for ${employeeName(employee)}`">
              <div v-for="location in employee.ActiveLocations" :key="location.id" class="employee-assignment" role="listitem">
                <v-icon small color="primary" class="employee-assignment__icon">mdi-store-outline</v-icon>
                <span class="employee-assignment__location" v-html="$options.filters.highlight(locationName(location), search)" />
                <span class="employee-assignment__role">{{ roleName(location) }}</span>
              </div>
              <div v-if="!employee.ActiveLocations || !employee.ActiveLocations.length" class="employee-assignment employee-assignment--empty admin-muted">
                <v-icon small class="employee-assignment__icon">mdi-store-off-outline</v-icon>No location access
              </div>
            </div>
          </v-card>
          <div v-if="!loading && count > options.itemsPerPage" class="employee-mobile-pagination mt-5">
            <div class="text-caption admin-muted text-center mb-2">{{ mobilePageRange }} of {{ count }}</div>
            <v-pagination
              v-model="options.page"
              :length="Math.ceil(count / options.itemsPerPage)"
              :total-visible="5"
              circle
              color="primary"
              aria-label="Employee pages"
            />
          </div>
        </div>

        <v-data-table
          v-else
          :headers="headers"
          :items="employees"
          :server-items-length="count"
          :loading="loading"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
          class="admin-data-table"
        >
          <template v-slot:item="{ item, index }">
            <tr>
              <td>#{{ (options.page - 1) * options.itemsPerPage + index + 1 }}</td>

              <td>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs">
                      {{ item.createdAt | moment("L") }}
                    </span>
                  </template>
                  <span>{{ item.createdAt | moment("L") }}</span>
                </v-tooltip>
              </td>

              <td class="text-xs-left small-td" v-html="$options.filters.highlight(item.firstName, search)" />
              <td class="text-xs-left small-td" v-html="$options.filters.highlight(item.lastName, search)" />

              <td class="text-xs-left">
                <div v-if="item.phoneNumber" v-html="$options.filters.highlight(item.phoneNumber, search)" />
                <div v-if="item.email" class="text-caption admin-muted" v-html="$options.filters.highlight(item.email, search)" />
                <span v-if="!item.phoneNumber && !item.email">N/A</span>
              </td>

              <!-- NEW: Active Locations + Roles -->
              <td class="text-xs-left employee-assignment-cell">
                <div class="employee-assignments" role="list" :aria-label="`Location assignments for ${employeeName(item)}`">
                  <div v-for="location in item.ActiveLocations" :key="location.id" class="employee-assignment" role="listitem">
                    <v-icon small color="primary" class="employee-assignment__icon">mdi-store-outline</v-icon>
                    <span class="employee-assignment__location" v-html="$options.filters.highlight(locationName(location), search)" />
                    <span class="employee-assignment__role">{{ roleName(location) }}</span>
                  </div>
                  <div v-if="!item.ActiveLocations || !item.ActiveLocations.length" class="employee-assignment employee-assignment--empty admin-muted">
                    <v-icon small class="employee-assignment__icon">mdi-store-off-outline</v-icon>No location access
                  </div>
                </div>
              </td>
              <td class="text-center px-0">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :aria-label="`Actions for ${employeeName(item)}`">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="editEmployee(item)">
                      <v-list-item-title> Edit </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!LocationId"
                      @click="deleteEmployee(item)"
                    >
                      <v-list-item-title>
                        {{ $t("buttons.delete") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div v-if="!loading && !employees.length" class="admin-empty-state py-10 text-center">
          <v-icon size="52" color="primary">mdi-account-search-outline</v-icon>
          <h3 class="text-h6 mt-3">{{ search ? 'No matching employees' : 'No employees yet' }}</h3>
          <p class="admin-muted">{{ search ? 'Try a different search term.' : 'Add an employee to get started.' }}</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import employeeService from "../service";

export default {
  name: "employee-list",
  props: ["LocationId", "hideHeader", "hideCreate"],

  data() {
    return {
      search: "",
      searchInput: "",
      employees: [],
      count: 0,
      loading: false,
      errorMessage: "",
      requestSequence: 0,
      rowsPerPageItems: [10, 25, 50, 100],

      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["activeLocationSort"], // default sort by first location name
        sortDesc: [false],
      },

      headers: [
        { text: "#" },
        { text: this.$t("labels.createdAt"), value: "createdAt" },
        { text: this.$t("labels.firstName"), value: "firstName" },
        { text: this.$t("labels.lastName"), value: "lastName" },
        { text: "Contact", value: "phoneNumber" },

        // NEW COLUMN
        { text: "Locations & roles", value: "activeLocationSort" },

        { text: "", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    mobilePageRange() {
      if (!this.count) return "0";
      const start = (this.options.page - 1) * this.options.itemsPerPage + 1;
      const end = Math.min(start + this.employees.length - 1, this.count);
      return `${start}–${end}`;
    },
  },

  watch: {
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },

  created() {
    this.getList();
  },

  methods: {
    applySearch() {
      this.search = String(this.searchInput || "").trim();
      if (this.options.page !== 1) this.options.page = 1;
      else this.getList();
    },
    clearSearch() {
      this.searchInput = "";
      this.search = "";
      if (this.options.page !== 1) this.options.page = 1;
      else this.getList();
    },
    employeeName(employee) { return [employee.firstName, employee.lastName].filter(Boolean).join(" ") || "Unnamed employee"; },
    initials(employee) { return [employee.firstName, employee.lastName].filter(Boolean).map((name) => name.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"; },
    locationName(location) { return (location.Location && location.Location.name) || "Unnamed location"; },
    roleName(location) { return (location.EmployeeRole && location.EmployeeRole.name) || "No role"; },

    async getList() {
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";

      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const offset = (page - 1) * itemsPerPage;
      const limit = itemsPerPage;
      const orderBy = [sortBy[0] || "firstName", sortDesc[0] || false];

      try {
        const response = await employeeService.getAll({
          limit,
          offset,
          search: this.search,
          local: true,
          orderBy,
          LocationId: this.LocationId,
          includeRole: true,
        });

        // ⭐ Add sortable field for new column
        if (requestId !== this.requestSequence) return;
        this.employees = (response.data || []).map((emp) => {
          const firstLoc = emp.ActiveLocations?.[0];
          return {
            ...emp,
            activeLocationSort: firstLoc?.name || "", // used for sorting
          };
        });

        this.count = response.count || 0;
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.employees = [];
        this.count = 0;
        this.errorMessage = "Employees could not be loaded. Please try again.";
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },

    editEmployee(employee) {
      this.$router.push({
        name: "employee-edit",
        params: { Employee: employee, LocationId: this.LocationId },
      });
    },

    deleteEmployee(employee) {
      this.$swal({
        title: this.$t("messages.title.areYouSure"),
        text: this.$t("messages.text.youWontBeAbleToRevert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: this.$t("buttons.cancel"),
        confirmButtonText: this.$t("buttons.yesDeleteIt"),
      }).then((result) => {
        if (result.value) {
          employeeService.delete(employee.id).then((response) => {
            if (response.data) {
              this.$swal(
                this.$t("messages.title.deleted"),
                this.$t("messages.text.employeeHasDelete"),
                "success"
              );
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.employee-heading-actions { align-items: center; display: flex; gap: 10px; }
.employee-search { align-items: center; display: flex; gap: 12px; }
.employee-search .admin-list-search { flex: 1; }
.employee-card__avatar { flex: 0 0 auto; }
.employee-assignment-cell { min-width: 280px; padding-top: 10px; padding-bottom: 10px; }
.employee-assignments { display: flex; flex-direction: column; gap: 6px; }
.employee-assignment {
  display: grid;
  grid-template-columns: 22px minmax(110px, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}
.employee-assignment__icon { justify-self: center; }
.employee-assignment__location { min-width: 0; line-height: 1.35; }
.employee-assignment__role {
  border: 1px solid var(--brand-primary);
  border-radius: 999px;
  color: var(--brand-primary);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  padding: 4px 8px;
  white-space: nowrap;
}
.employee-assignment--empty { grid-template-columns: 22px 1fr; font-size: 0.78rem; }
@media (max-width: 599px) {
  .employee-heading-actions { align-items: stretch; flex-direction: column; width: 100%; }
  .employee-heading-actions .v-btn { margin: 0; width: 100%; }
  .employee-search { align-items: stretch; flex-direction: column; }
  .employee-search .v-btn { width: 100%; }
  .employee-assignment { grid-template-columns: 22px minmax(0, 1fr) auto; }
}
</style>
